/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import * as ReactDOM from "react-dom";
import { FaExclamationTriangle } from "react-icons/fa";
import { ThemeType } from "../DesignSystem/Theme/Theme";
import { ValidationResult } from "./QuickFormValidationRules";
import { TOP_ITEM_ZINDEX } from "../Utils/SiteProps";

interface QuickFormValidationIconProps {
  theme: ThemeType;
  validationResult: ValidationResult;
}

export const QuickFormValidationIcon: React.FC<
  QuickFormValidationIconProps
> = ({ theme, validationResult }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const iconRef = React.useRef<HTMLDivElement>(null);

  const updatePosition = React.useCallback((): void => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, []);

  // Keep position in sync if the table scrolls or the window resizes
  // while the tooltip is open.
  React.useEffect(() => {
    if (!showTooltip) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [showTooltip, updatePosition]);

  if (validationResult.isValid) {
    return null;
  }

  return (
    <div
      ref={iconRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <FaExclamationTriangle
        size={16}
        color={theme.colors.validationErrorIcon}
        style={{ cursor: "help" }}
      />

      {showTooltip &&
        tooltipPosition &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: "translate(-50%, calc(-100% - 8px))",
              padding: "12px",
              background: theme.colors.tooltipBackground,
              color: theme.colors.tooltipText,
              borderRadius: "6px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              zIndex: TOP_ITEM_ZINDEX,
              minWidth: "250px",
              maxWidth: "350px",
              fontSize: "13px",
              whiteSpace: "normal",
              fontFamily: theme.fonts.primary,
              pointerEvents: "none",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>
              Cannot Transform:
            </div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {validationResult.violations.map((violation, index) => (
                <li key={index} style={{ marginBottom: "4px" }}>
                  {violation.message}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};
